import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import type { SharePointActivity, SharePointResultsPart, SharePointSource } from './types';

interface MergedSource extends SharePointSource {
  read: boolean;
}

// One answer can search and then read several documents; showing a card per
// tool call would stack near-duplicates, so they are merged per message.
export function mergeSharePointSources(parts: SharePointResultsPart[]): MergedSource[] {
  const byUrl = new Map<string, MergedSource>();
  for (const part of parts) {
    for (const source of part.results) {
      const key = source.url || source.title;
      const existing = byUrl.get(key);
      const read = part.action === 'read' || !!existing?.read;
      byUrl.set(key, { ...existing, ...source, summary: source.summary || existing?.summary, read });
    }
  }

  return Array.from(byUrl.values()).sort((a, b) => Number(b.read) - Number(a.read));
}

const formatDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);

  return isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const SharePointIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="8" y1="13" x2="16" y2="13"/>
    <line x1="8" y1="17" x2="13" y2="17"/>
  </svg>
);

const ACTIVITY_LABELS: Record<SharePointActivity, string> = {
  searching: 'Searching SharePoint…',
  reading: 'Reading in SharePoint…',
  browsing: 'Browsing SharePoint…',
  summarizing: 'Summarising a long document, part by part…',
  comparing: 'Comparing document versions…',
};

export const SharePointStatus: React.FC<{ status: SharePointActivity }> = ({ status }) => {
  const { colors } = useTheme();

  return (
    <div style={{
      margin: '8px 0',
      padding: '8px 12px',
      backgroundColor: colors.surfaceElevated,
      borderRadius: '8px',
      border: `1px solid ${colors.borderLight}`,
      fontSize: '13px',
      color: colors.textSecondary,
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }}>
      <SharePointIcon />
      <span>{ACTIVITY_LABELS[status] ?? ACTIVITY_LABELS.searching}</span>
    </div>
  );
};

const SharePointResults: React.FC<{ sources: MergedSource[] }> = ({ sources }) => {
  const { colors } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  if (sources.length === 0) {
    return null;
  }

  const readCount = sources.filter((s) => s.read).length;

  return (
    <div style={{
      margin: '12px 0',
      backgroundColor: colors.surface,
      borderRadius: '12px',
      overflow: 'hidden',
      border: `1px solid ${colors.border}`,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    }}>
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '12px 16px',
          borderBottom: isExpanded ? `1px solid ${colors.borderLight}` : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: colors.surfaceElevated,
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.textPrimary }}>
          <SharePointIcon />
          <span style={{ fontWeight: 600, fontSize: '14px' }}>SharePoint sources</span>
          <span style={{
            fontSize: '12px',
            color: colors.textMuted,
            marginLeft: '6px',
            backgroundColor: colors.surface,
            padding: '2px 6px',
            borderRadius: '12px'
          }}>
            {readCount > 0 ? `${readCount} read · ` : ''}{sources.length} {sources.length === 1 ? 'document' : 'documents'}
          </span>
        </div>

        <span style={{
          fontSize: '12px',
          color: colors.textMuted,
          transition: 'transform 0.2s ease',
          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
        }}>
          ▶
        </span>
      </div>

      {isExpanded && (
        <div>
          {sources.map((source, index) => {
            const isSafeLink = source.url.startsWith('https://');
            const meta = [source.site, formatDate(source.last_modified)].filter(Boolean).join(' · ');

            return (
              <div key={index} style={{
                padding: '12px 16px',
                borderBottom: index < sources.length - 1 ? `1px solid ${colors.borderLight}` : 'none',
              }}
              className="sharepoint-result-item"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  {isSafeLink ? (
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: colors.primary, textDecoration: 'none', fontWeight: 500, fontSize: '14px' }}
                    >
                      {source.title}
                    </a>
                  ) : (
                    <span style={{ fontWeight: 500, fontSize: '14px', color: colors.textPrimary }}>{source.title}</span>
                  )}
                  {source.kind === 'page' && (
                    <span style={{
                      fontSize: '11px',
                      color: colors.textSecondary,
                      backgroundColor: colors.surfaceElevated,
                      border: `1px solid ${colors.borderLight}`,
                      padding: '1px 7px',
                      borderRadius: '12px',
                    }}>
                      Page
                    </span>
                  )}
                  {source.read && (
                    <span style={{
                      fontSize: '11px',
                      color: '#0f766e',
                      backgroundColor: '#ccfbf1',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontWeight: 500,
                    }}>
                      Read
                    </span>
                  )}
                </div>
                {source.summary && (
                  <div style={{ fontSize: '13px', color: colors.textPrimary, lineHeight: '1.4', margin: '4px 0' }}>
                    {source.summary}
                  </div>
                )}
                {meta && (
                  <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>{meta}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .sharepoint-result-item:hover {
          background-color: ${colors.buttonHover};
        }
        .sharepoint-result-item a:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default SharePointResults;
